import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DatepickerOptions } from 'ng2-datepicker';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { environment } from './../../../environments/environment';
import { AccessGroupService } from './../access-group.service';

@Component({
  selector: 'app-admin-learner-maps-list',
  templateUrl: './admin-learner-maps-list.component.html',
  styleUrls: ['./admin-learner-maps-list.component.scss']
})
export class AdminLearnerMapsListComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  public allmaps: any[] = [];
  public allmaptypes: any[] = [];
  public isLoading = true;
  public totalLms = 0;
  public paginationArr: number[] = [];
  public currentPage = 1;
  public perPage = 20;
  public sortColumn = 'title';
  public sortDirection: 'asc' | 'desc' = 'asc';

  // Inline edit state
  public editLm: any = null;
  public editMapName = '';
  public edit_maptype: any = '';
  public edit_Owner: any = '';
  public edit_location = '';
  public editLmType: any = null;
  public editMapTypeName = '';

  // Title feature parsing
  public feature_grp: string[] = [];
  public feature_grp_prod: string[] = [];
  public lm_titles: string[] = [];

  // Search
  public searchMap = '';

  // New learner map form fields
  public new_map: any = '';
  public new_maptype: any = '';
  public new_mapType = '';
  public new_mapLocation = '';
  public new_mapImage = '';
  public prework_days: number | null = null;
  public assessment_deadline: number | null = null;
  public before_day_point: number | null = null;
  public days_test_opens: number | null = null;
  public days_test_close: number | null = null;
  public first_training: string = new Date().toDateString();
  public last_training: string = new Date().toDateString();
  public createdAt: string = new Date().toDateString();
  public map_owner: any = '';

  // Modal visibility flags
  public showAddLmModal = false;
  public showAddLmTypeModal = false;
  public showListLmTypeModal = false;

  breadCrumbFor = '';

  options: DatepickerOptions = {
    minYear: 1970,
    maxYear: 2030,
    format: 'DD-MM-YYYY',
    formatTitle: 'DD-MM-YYYY',
    firstCalendarDay: 0,
    placeholder: 'Click to select a date',
    inputClass: 'form-control',
  };

  private readonly apiBase = environment.profileURL;

  constructor(
    private accessgroup: AccessGroupService,
    private router: Router,
    public store: Store<any>,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.store.select<any>((state) => state.profile.accessGroupData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        // Allow access if Redux store has data, don't require specific learner_maps_admin flag
        // as it may not always be populated
      });

    this.searchSubject$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((term) => {
        if (term.length >= 3) {
          this.searchLearnerMaps(term);
        } else if (term.length === 0) {
          this.AllLearnerMaps();
        }
      });

    this.AllLearnerMaps();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data Loading ---

  public AllLearnerMaps(page = 1): void {
    this.currentPage = page;
    this.isLoading = true;
    this.map_owner = localStorage.getItem('email') || '';

    forkJoin({
      maps: this.http.get<any>(`${this.apiBase}/api/v1/learner_maps?page=${page}&per_page=${this.perPage}`),
      types: this.http.get<any>(`${this.apiBase}/api/v1/learner_map_types`)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ maps, types }) => {
          this.totalLms = maps?.meta?.total_count ?? 0;
          const totalPages = Math.ceil(this.totalLms / this.perPage);
          this.paginationArr = Array.from({ length: totalPages }, (_, i) => i + 1);

          const sortedMaps = (maps?.learner_maps ?? []).sort((a: any, b: any) =>
            a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
          );

          this.parseTitleFeatures(sortedMaps);
          this.allmaps = sortedMaps;
          this.allmaptypes = types?.learner_map_types ?? [];
          this.isLoading = false;
        },
        error: () => {
          Swal.fire('Error', 'Failed to load learner maps.', 'error');
          this.isLoading = false;
        }
      });
  }

  private searchLearnerMaps(title: string): void {
    this.isLoading = true;
    this.http.get<any>(`${this.apiBase}/api/v1/learner_maps?title=${encodeURIComponent(title)}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.allmaps = res?.learner_maps ?? [];
          this.parseTitleFeatures(this.allmaps);
          this.isLoading = false;
        },
        error: () => {
          Swal.fire('Error', 'Search failed.', 'error');
          this.isLoading = false;
        }
      });
  }

  private parseTitleFeatures(maps: any[]): void {
    this.feature_grp = [];
    this.feature_grp_prod = [];
    this.lm_titles = [];

    maps.forEach((record, index) => {
      const parts = record.title.split('#');
      const title = (parts[0] || '').trim();
      const featureStaging = (parts[1] || '').trim();
      const featureProduction = (parts.slice(2).join('#') || '').trim();

      maps[index].title = title;
      this.lm_titles.push(title);
      this.feature_grp.push(featureStaging);
      this.feature_grp_prod.push(featureProduction);
    });
  }

  // --- Sorting ---

  public toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortMaps();
  }

  private sortMaps(): void {
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.allmaps.sort((a, b) => {
      let valA = '';
      let valB = '';

      switch (this.sortColumn) {
        case 'title':
          valA = (a.title || '').toLowerCase();
          valB = (b.title || '').toLowerCase();
          break;
        case 'type':
          valA = (a.learner_map_type_title || a.maptype?.title || '').toLowerCase();
          valB = (b.learner_map_type_title || b.maptype?.title || '').toLowerCase();
          break;
        case 'owner':
          valA = (a.owner_members_emails || a.owners || '').toLowerCase();
          valB = (b.owner_members_emails || b.owners || '').toLowerCase();
          break;
        case 'location':
          valA = (a.location || '').toLowerCase();
          valB = (b.location || '').toLowerCase();
          break;
      }

      return valA.localeCompare(valB) * dir;
    });
  }

  // --- Search ---

  public filterMaps(): void {
    this.searchSubject$.next(this.searchMap);
  }

  public resetName(): void {
    this.searchMap = '';
    this.AllLearnerMaps();
  }

  // --- CRUD: Learner Maps ---

  public NewLearnerMap(): void {
    if (!this.new_map || !this.new_mapLocation || !this.new_maptype) {
      return;
    }

    const data = {
      title: this.new_map,
      owners: (this.map_owner || '').toLowerCase(),
      learner_map_type_id: this.new_maptype,
      location: this.new_mapLocation,
      image: this.new_mapImage,
      days_of_prework: this.prework_days,
      days_before_assessment_deadline: this.assessment_deadline,
      days_before_point_due: this.before_day_point,
      days_after_training_before_test_opens: this.days_test_opens,
      days_after_training_before_test_closes: this.days_test_close,
      createdAt: new Date(this.createdAt).toISOString(),
      training_first_date: new Date(this.first_training).toISOString(),
      training_last_date: new Date(this.last_training).toISOString()
    };

    this.http.post(`${this.apiBase}/api/v1/learner_maps`, data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showAddLmModal = false;
          this.new_map = '';
          Swal.fire('Nice!', 'New learner map has been created!', 'success');
          this.AllLearnerMaps();
        },
        error: () => {
          Swal.fire('Oops!', 'Failed to create learner map.', 'error');
        }
      });
  }

  public DeleteLearnerMap(lmid: string, _index?: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to delete this Learner Map? This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#28a745',
      confirmButtonText: 'Yes, delete it!'
    }).then((result: any) => {
      if (!result.value) {
        return;
      }

      this.isLoading = true;
      const ownerMemberIds: any[] = [];
      const map = this.allmaps.find((m) => m.id === lmid);
      if (map?.owner_members) {
        map.owner_members.forEach((owner: any) => ownerMemberIds.push(owner.id));
      }

      this.http.post(`${this.apiBase}/api/v1/learner_maps/${lmid}/remove_owner_members`, { owner_member_ids: ownerMemberIds })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.http.delete(`${this.apiBase}/api/v1/learner_maps/${lmid}`)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  this.allmaps = this.allmaps.filter((m) => m.id !== lmid);
                  Swal.fire('Nice!', 'Your learner map has been deleted!', 'success');
                  this.isLoading = false;
                },
                error: (err) => {
                  Swal.fire('Oops!', err?.error?.errors || 'Failed to delete learner map.', 'error');
                  this.isLoading = false;
                }
              });
          },
          error: () => {
            Swal.fire('Oops!', 'Failed to remove owners before deletion.', 'error');
            this.isLoading = false;
          }
        });
    });
  }

  public cloneMap(map: any): void {
    this.isLoading = true;

    this.http.post(`${this.apiBase}/api/v1/learner_maps/${map.id}/clone.json`, { title: 'clone ' + map?.title })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Swal.fire('Nice!', 'Your learner map has been cloned!', 'success');
          this.AllLearnerMaps();
        },
        error: () => {
          Swal.fire('Oops!', 'Failed to clone learner map.', 'error');
          this.isLoading = false;
        }
      });
  }

  public editMap(lm: any): void {
    this.editLm = lm.id;
    this.editMapName = lm?.title ?? '';
    this.edit_maptype = lm?.learner_map_type_id ? String(lm.learner_map_type_id) : '';
    this.edit_Owner = lm?.owners ?? '';
    this.edit_location = lm?.location ?? '';
  }

  public cancelSave(): void {
    this.editLm = null;
    this.editMapName = '';
  }

  public saveMap(map: any, i: number): void {
    if (!this.editMapName || !this.edit_maptype || !this.edit_location) {
      Swal.fire('Oops!', 'Invalid input!', 'error');
      return;
    }

    const data = {
      title: this.editMapName.replace(/[\[\]]/g, ''),
      learner_map_type_id: this.edit_maptype,
      owners: (this.edit_Owner || '').toLowerCase().replace(/[\[\]]/g, ''),
      location: (this.edit_location || '').replace(/[\[\]]/g, '')
    };

    this.http.patch<any>(`${this.apiBase}/api/v1/learner_maps/${map.id}`, data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.editLm = null;
          this.allmaps[i] = res?.learner_map;
          Swal.fire('Nice!', 'Your learner map has been updated!', 'success');
        },
        error: () => {
          Swal.fire('Oops!', 'Failed to update learner map.', 'error');
        }
      });
  }

  // --- CRUD: Learner Map Types ---

  public editMapType(lmtype: any): void {
    this.editLmType = lmtype.id;
    this.editMapTypeName = lmtype.title;
  }

  public cancelSaveLMType(): void {
    this.editLmType = null;
    this.editMapTypeName = '';
  }

  public saveMapType(type: any, i: number): void {
    this.http.patch<any>(`${this.apiBase}/api/v1/learner_map_types/${type.id}`, { title: this.editMapTypeName })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.cancelSaveLMType();
          this.allmaptypes[i] = res?.learner_map_type;
          Swal.fire('Nice!', 'Your map type has been updated!', 'success');
        },
        error: () => {
          Swal.fire('Oops!', 'Failed to update map type.', 'error');
        }
      });
  }

  public DeleteLearnerMapType(lmtypeid: any): void {
    // Close the list modal so Swal shows on top
    this.showListLmTypeModal = false;

    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to delete this Learner Map Type? This cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#28a745',
      confirmButtonText: 'Yes, delete it!'
    }).then((result: any) => {
      if (!result.value) {
        // Reopen the list modal if user cancelled
        this.showListLmTypeModal = true;
        return;
      }

      this.http.delete(`${this.apiBase}/api/v1/learner_map_types/${lmtypeid}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.allmaptypes = this.allmaptypes.filter((t) => t.id !== lmtypeid);
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'Your map type has been deleted.',
              timer: 2000,
              showConfirmButton: false
            }).then(() => {
              this.showListLmTypeModal = true;
            });
          },
          error: (err) => {
            Swal.fire({
              icon: 'error',
              title: 'Oops!',
              text: err?.error?.message || 'Failed to delete map type.',
              timer: 3000
            }).then(() => {
              this.showListLmTypeModal = true;
            });
          }
        });
    });
  }

  public addLMType(): void {
    if (!this.new_mapType) {
      return;
    }

    this.http.post(`${this.apiBase}/api/v1/learner_map_types`, { title: this.new_mapType })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showAddLmTypeModal = false;
          this.new_mapType = '';
          Swal.fire('Nice!', 'New map type has been added!', 'success');
          this.AllLearnerMaps();
        },
        error: (err) => {
          Swal.fire('Oops!', err?.error?.message || 'Something went wrong!', 'error');
        }
      });
  }

  // --- Modal Controls ---
  // These use jQuery to match the current template's CSS-class-based modals.
  // A future task should replace these with boolean flags + *ngIf in the template.

  public open(content: string, lm: boolean = false): void {
    if (content === 'addlm') {
      this.showAddLmModal = true;
    }
    this.new_map = '';
    this.new_maptype = '';
    this.new_mapImage = '';
    this.new_mapLocation = '';
    this.prework_days = null;
    this.assessment_deadline = null;
    this.before_day_point = null;
    this.days_test_opens = null;
    this.days_test_close = null;
    this.createdAt = new Date().toDateString();
    this.first_training = new Date().toDateString();
    this.last_training = new Date().toDateString();
  }

  public openLMtype(lmtype: boolean = false): void {
    this.showAddLmTypeModal = true;
  }

  public listLMtype(lmtype: boolean = false): void {
    this.editLmType = null;
    this.showListLmTypeModal = true;
  }

  public removedSquareBrackets(str: string): string {
    return str.replace(/[\[\]]/g, '');
  }
}
